import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "../components/Navigation";
import {
  Box,
  Typography,
  Button,
  FormLabel,
  Select,
  MenuItem,
  Switch,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
import { Divider } from "@mui/material";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Footer from "../components/Footer";
import useSnackbar from "../hooks/useSnackbar";
import SnackbarAlert from "../components/SnackbarAlert";

const StyledTooltip = withStyles(() => ({
  tooltip: {
    fontSize: "0.8rem",
    fontFamily: "Poppins, sans-serif",
    backgroundColor: "#2a2a2a",
    color: "rgba(255,255,255,0.85)",
    padding: "10px 14px",
    maxWidth: 260,
    lineHeight: 1.5,
    borderRadius: 6,
    border: "1px solid rgba(255,255,255,0.1)",
  },
  arrow: {
    color: "#2a2a2a",
  },
}))(Tooltip);

const DEFAULTS = {
  response_length: "auto",
  temperature: "precise",
  crawl_depth: 2,
  streaming: true,
  expand_responses: false,
};

const STORAGE_KEY = "ace_user_settings";

const loadSettings = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? { ...DEFAULTS, ...JSON.parse(stored) } : { ...DEFAULTS };
  } catch {
    return { ...DEFAULTS };
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.9)",
    padding: theme.spacing(0.2),
  },
  container: {
    display: "flex",
    justifyContent: "center",
    margin: "auto",
    width: "100%",
    marginTop: theme.spacing(16),
    paddingBottom: theme.spacing(6),
    [theme.breakpoints.down("md")]: {
      marginTop: theme.spacing(14),
    },
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(12),
    },
  },
  card: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: 480,
    [theme.breakpoints.down("sm")]: {
      width: "90%",
    },
  },
  title: {
    color: "#ffffff",
    fontSize: "1.3rem",
    fontWeight: "bold",
    fontFamily: "Raleway, sans-serif",
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    color: "rgba(255,255,255,0.4)",
    fontSize: "0.8rem",
    fontFamily: "Poppins, sans-serif",
    marginBottom: theme.spacing(4),
  },
  fieldRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(3),
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  label: {
    color: "rgba(255,255,255,0.85)",
    fontFamily: "Poppins, sans-serif",
    fontWeight: 500,
    fontSize: "0.85rem",
  },
  infoIcon: {
    fontSize: 15,
    color: "rgba(255,255,255,0.3)",
    cursor: "help",
  },
  select: {
    minWidth: 150,
    backgroundColor: "rgba(255,255,255,0.06)",
    borderRadius: 6,
    color: "white",
    fontFamily: "Poppins, sans-serif",
    fontSize: "0.8rem",
    "& .MuiSelect-icon": {
      color: "rgba(255,255,255,0.4)",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,198,41,0.25)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,198,41,0.4)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,198,41,0.5)",
    },
  },
  menuItem: {
    fontFamily: "Poppins, sans-serif",
    fontSize: "0.8rem",
  },
  switchTrack: {
    backgroundColor: "rgba(255,198,41,0.35) !important",
  },
  buttonRow: {
    display: "flex",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(4),
  },
  buttonSave: {
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "#ffffff",
    borderRadius: 8,
    padding: theme.spacing(1, 3),
    fontSize: "0.85rem",
    fontWeight: 500,
    textTransform: "none",
    fontFamily: "Poppins, sans-serif",
    border: "1px solid rgba(255,255,255,0.12)",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.15)",
    },
  },
  buttonReset: {
    backgroundColor: "transparent",
    color: "rgba(255,255,255,0.4)",
    borderRadius: 8,
    padding: theme.spacing(1, 3),
    fontSize: "0.85rem",
    fontWeight: 400,
    textTransform: "none",
    fontFamily: "Poppins, sans-serif",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.04)",
      color: "rgba(255,255,255,0.6)",
    },
  },
}));

const TOOLTIPS = {
  response_length:
    "Auto lets ACE decide the best length. Short (~150-250 words), Medium (~400-600 words), Long (~800-1200 words). If your question asks for more detail, ACE will adapt.",
  temperature:
    "Precise keeps responses factual and consistent. Creative allows more varied, expressive responses. Balanced is in between.",
  crawl_depth:
    "When your collection contains URLs, this controls how many levels deep ACE will follow links. Higher values retrieve more content but take longer.",
  streaming:
    "When enabled, text appears word-by-word as it's generated. When disabled, the full response loads at once.",
  expand_responses:
    "When enabled, responses are always shown in full. When disabled, long responses are collapsed with a 'Read more' button.",
};

function UserSettings() {
  const classes = useStyles();
  const navigate = useNavigate();
  const {
    snackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    showSnackbar,
    handleCloseSnackbar,
  } = useSnackbar();
  const [settings, setSettings] = useState(loadSettings);
  const savedSettingsRef = useRef(loadSettings());
  const [unsavedDialog, setUnsavedDialog] = useState(false);
  const pendingNavRef = useRef(null);

  const hasUnsavedChanges = useCallback(() => {
    return JSON.stringify(settings) !== JSON.stringify(savedSettingsRef.current);
  }, [settings]);

  // Warn on browser close/refresh with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Intercept in-app navigation via link clicks
  useEffect(() => {
    const handleClick = (e) => {
      const link = e.target.closest("a[href]");
      if (!link) return;
      const href = link.getAttribute("href");
      if (!href || href.startsWith("http") || href === "#") return;
      if (hasUnsavedChanges()) {
        e.preventDefault();
        pendingNavRef.current = href;
        setUnsavedDialog(true);
      }
    };
    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [hasUnsavedChanges]);

  const handleDialogDiscard = () => {
    setUnsavedDialog(false);
    const dest = pendingNavRef.current;
    pendingNavRef.current = null;
    if (dest) navigate(dest);
  };

  const handleDialogSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    savedSettingsRef.current = { ...settings };
    setUnsavedDialog(false);
    showSnackbar("Settings saved", "success");
    const dest = pendingNavRef.current;
    pendingNavRef.current = null;
    if (dest) navigate(dest);
  };

  const handleDialogCancel = () => {
    setUnsavedDialog(false);
    pendingNavRef.current = null;
  };

  const update = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    savedSettingsRef.current = { ...settings };
    showSnackbar("Settings saved", "success");
  };

  const handleReset = () => {
    setSettings({ ...DEFAULTS });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULTS));
    savedSettingsRef.current = { ...DEFAULTS };
    showSnackbar("Settings reset to defaults", "info");
  };

  return (
    <Box className={classes.root}>
      <Navbar />
      <SnackbarAlert
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleCloseSnackbar}
      />
      <Box className={classes.container}>
        <Box className={classes.card}>
          <Typography className={classes.title}>Settings</Typography>
          <Typography className={classes.subtitle}>
            These preferences are applied to every new message you send to ACE.
          </Typography>

          {/* Response Length */}
          <Box className={classes.fieldRow}>
            <Box className={classes.labelRow}>
              <Typography className={classes.label}>Response length</Typography>
              <StyledTooltip title={TOOLTIPS.response_length} placement="top" arrow>
                <InfoOutlinedIcon className={classes.infoIcon} />
              </StyledTooltip>
            </Box>
            <Select
              value={settings.response_length}
              onChange={(e) => update("response_length", e.target.value)}
              variant="outlined"
              className={classes.select}
              MenuProps={{
                PaperProps: {
                  style: {
                    backgroundColor: "#2a2a2a",
                    color: "white",
                  },
                },
              }}
            >
              <MenuItem value="auto" className={classes.menuItem}>
                Auto
              </MenuItem>
              <MenuItem value="short" className={classes.menuItem}>
                Short
              </MenuItem>
              <MenuItem value="medium" className={classes.menuItem}>
                Medium
              </MenuItem>
              <MenuItem value="long" className={classes.menuItem}>
                Long
              </MenuItem>
            </Select>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.06)", mb: 3 }} />

          {/* Creativity */}
          <Box className={classes.fieldRow}>
            <Box className={classes.labelRow}>
              <Typography className={classes.label}>Creativity</Typography>
              <StyledTooltip title={TOOLTIPS.temperature} placement="top" arrow>
                <InfoOutlinedIcon className={classes.infoIcon} />
              </StyledTooltip>
            </Box>
            <Select
              value={settings.temperature}
              onChange={(e) => update("temperature", e.target.value)}
              variant="outlined"
              className={classes.select}
              MenuProps={{
                PaperProps: {
                  style: {
                    backgroundColor: "#2a2a2a",
                    color: "white",
                  },
                },
              }}
            >
              <MenuItem value="precise" className={classes.menuItem}>
                Precise
              </MenuItem>
              <MenuItem value="balanced" className={classes.menuItem}>
                Balanced
              </MenuItem>
              <MenuItem value="creative" className={classes.menuItem}>
                Creative
              </MenuItem>
            </Select>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.06)", mb: 3 }} />

          {/* Crawl Depth */}
          <Box className={classes.fieldRow}>
            <Box className={classes.labelRow}>
              <Typography className={classes.label}>Crawl depth</Typography>
              <StyledTooltip title={TOOLTIPS.crawl_depth} placement="top" arrow>
                <InfoOutlinedIcon className={classes.infoIcon} />
              </StyledTooltip>
            </Box>
            <Select
              value={settings.crawl_depth}
              onChange={(e) => update("crawl_depth", e.target.value)}
              variant="outlined"
              className={classes.select}
              MenuProps={{
                PaperProps: {
                  style: {
                    backgroundColor: "#2a2a2a",
                    color: "white",
                  },
                },
              }}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <MenuItem key={n} value={n} className={classes.menuItem}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.06)", mb: 3 }} />

          {/* Streaming */}
          <Box className={classes.fieldRow}>
            <Box className={classes.labelRow}>
              <Typography className={classes.label}>
                Stream responses
              </Typography>
              <StyledTooltip title={TOOLTIPS.streaming} placement="top" arrow>
                <InfoOutlinedIcon className={classes.infoIcon} />
              </StyledTooltip>
            </Box>
            <Switch
              checked={settings.streaming}
              onChange={(e) => update("streaming", e.target.checked)}
              size="small"
              style={{ color: settings.streaming ? "#FFC629" : undefined }}
              classes={{
                track: classes.switchTrack,
              }}
            />
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.06)", mb: 3 }} />

          {/* Expand Responses */}
          <Box className={classes.fieldRow}>
            <Box className={classes.labelRow}>
              <Typography className={classes.label}>
                Always show full responses
              </Typography>
              <StyledTooltip title={TOOLTIPS.expand_responses} placement="top" arrow>
                <InfoOutlinedIcon className={classes.infoIcon} />
              </StyledTooltip>
            </Box>
            <Switch
              checked={settings.expand_responses}
              onChange={(e) => update("expand_responses", e.target.checked)}
              size="small"
              style={{ color: settings.expand_responses ? "#FFC629" : undefined }}
              classes={{
                track: classes.switchTrack,
              }}
            />
          </Box>

          {/* Actions */}
          <Box className={classes.buttonRow}>
            <Button className={classes.buttonSave} onClick={handleSave}>
              Save
            </Button>
            <Button className={classes.buttonReset} onClick={handleReset}>
              Reset to defaults
            </Button>
          </Box>
        </Box>
      </Box>
      <Footer />

      {/* Unsaved changes dialog */}
      <Dialog
        open={unsavedDialog}
        onClose={handleDialogCancel}
        PaperProps={{
          style: {
            backgroundColor: "#1e1e1e",
            color: "white",
            borderRadius: 12,
            border: "1px solid rgba(255,255,255,0.1)",
          },
        }}
      >
        <DialogTitle style={{ fontFamily: "Raleway, sans-serif", fontWeight: "bold" }}>
          Unsaved changes
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ color: "rgba(255,255,255,0.6)", fontFamily: "Poppins, sans-serif", fontSize: "0.85rem" }}>
            You have unsaved changes. Would you like to save before leaving?
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: "8px 24px 16px" }}>
          <Button onClick={handleDialogCancel} style={{ color: "rgba(255,255,255,0.4)", textTransform: "none", fontFamily: "Poppins, sans-serif" }}>
            Cancel
          </Button>
          <Button onClick={handleDialogDiscard} style={{ color: "#ff6b6b", textTransform: "none", fontFamily: "Poppins, sans-serif" }}>
            Discard
          </Button>
          <Button onClick={handleDialogSave} style={{ color: "#FFC629", textTransform: "none", fontFamily: "Poppins, sans-serif", fontWeight: 500 }}>
            Save & leave
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserSettings;
export { STORAGE_KEY, DEFAULTS, loadSettings };
